import type { ReactNode } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import { INTL_SHORT_DAY_NAME_DAY_MONTH } from 'constants/dateFormats'
import styled from 'styled-components'
import BodyText from 'LuxKit/Typography/BodyText'
import LineRefundIcon from 'LuxKit/Icons/line/LineRefundIcon'
import TextLink from 'LuxKit/TextLink'
import Clickable from 'components/Common/Clickable'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import ModalContext from 'contexts/ModalContext'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import OfferPropertyFlexibleCancellationPolicyTimelineModal from './OfferPropertyFlexibleCancellationPolicyTimelineModal'
import { useIntl } from 'react-intl'
import Colour from 'components/utils/Colour'
import useRebrandSuccessDarkColour from 'hooks/useRebrandSuccessDarkColour'
import type { FlexibleCancellationTimelineDetails } from './flexibleCancellationPolicyTimelineUtils'

const MiddledTooltipIcon = styled(LineInfoCircleIcon)`
  vertical-align: middle;
`

const FLEXIBLE_CANCELLATION_TOOLTIP_ICON = <MiddledTooltipIcon size="XS" />

interface Props {
  details: FlexibleCancellationTimelineDetails
  isCharter: boolean
  isFlashUSP?: boolean
}

/**
 * Presentational flexible-cancellation USP/link. No Redux or eligibility derivation —
 * callers pass a resolved timeline context (or fields from one).
 */
function OfferPropertyFlexibleCancellationPoliciesView(props: Props) {
  const { details, isCharter, isFlashUSP } = props
  const intl = useIntl()
  const uspColour = useRebrandSuccessDarkColour()
  const intlDateFormatter = useIntlDateFormatter()
  const showModal = useContext(ModalContext)

  const {
    startBasedOnCheckIn,
    checkInDate: detailsCheckInDate,
  } = details

  const onItemClick = useCallback<React.MouseEventHandler<HTMLAnchorElement>>((e) => {
    e.preventDefault()
    showModal(
      <OfferPropertyFlexibleCancellationPolicyTimelineModal
        details={details}
        isCharter={isCharter}
      />,
    )
  }, [details, isCharter, showModal])

  const hotelOnlyText = intl.formatMessage({
    id: 'rw2sVUpb_T8GWv5U-HY8',
    defaultMessage: '(Hotel only)',
    description: 'Text indicating hotel-only cancellation policy in the cancellation flow',
  })

  const formatFlexibleCancellationBold = useCallback((chunks: React.ReactNode) => (
    <Colour key="flexible-cancellation-bold" value={uspColour} as="b">{chunks}</Colour>
  ), [uspColour])

  const richTextValues = useMemo(() => ({
    hotelOnlyText,
    bold: formatFlexibleCancellationBold,
    tooltipIcon: FLEXIBLE_CANCELLATION_TOOLTIP_ICON,
  }), [hotelOnlyText, formatFlexibleCancellationBold])

  let cancellationPolicyText: ReactNode = null

  if (detailsCheckInDate && startBasedOnCheckIn) {
    cancellationPolicyText = intl.formatMessage({
      id: 'w-0YL7uQj4115MK8yWse',
      defaultMessage: '<bold>Flexible cancellation</bold> until {date} {hotelOnlyText} {tooltipIcon}',
      description: 'Text for flexible cancellation until a specific date with tooltip icon',
    },
    {
      ...richTextValues,
      date: intlDateFormatter(startBasedOnCheckIn, INTL_SHORT_DAY_NAME_DAY_MONTH),
    })
  } else if (!detailsCheckInDate) {
    cancellationPolicyText = intl.formatMessage({
      id: '3ybsKWkjCx-vRQgsh0Jb',
      defaultMessage: '<bold>Flexible cancellation</bold> up to 7 days after purchase {hotelOnlyText} {tooltipIcon}',
      description: 'Text for flexible cancellation up to 7 days after purchase with tooltip icon',
    }, richTextValues)
  } else {
    cancellationPolicyText = intl.formatMessage({
      id: '8IHlrASOTyw2pMMzFBE-',
      defaultMessage: '<bold>Flexible cancellation</bold> {tooltipIcon}',
      description: 'Label for flexible cancellation policy with tooltip icon',
    }, richTextValues)
  }

  const flexibleCancellationLinkLabel = intl.formatMessage({
    id: 'TPCgs5T7ix7LMSAopVJI',
    defaultMessage: 'Flexible cancellation',
    description: 'Link text for flexible cancellation policy in the cancellation flow',
  })

  return <>
    {!isFlashUSP &&
      <Clickable onClick={onItemClick}>
        <BodyText variant="medium" as="span">
          {cancellationPolicyText}
        </BodyText>
      </Clickable>}
    {isFlashUSP &&
      <TextLink
        size="medium"
        colour={uspColour}
        underline={false}
        data-type="7day"
        onClick={onItemClick}
        startIcon={<LineRefundIcon />}
      >
        {flexibleCancellationLinkLabel}
      </TextLink>}
  </>
}

export default OfferPropertyFlexibleCancellationPoliciesView
